<template>
  <div class="comparison-plan-section">
    <div class="comparison-plan-section__trial" v-if="showTrialNotice">
      <free-trial-notice-parts />
    </div>

    <div class="comparison-plan-section__head">
      <h4 v-if="!showTrialNotice" class="comparison-plan-section__title" v-html="sectionTitle" />
      <!-- JRPからの要望でアプリ内課金ではポイントが付与されないという文言をマスク。復活させる可能性はあるため、コードは残しておく。 -->
      <template v-if="false">
        <p v-if="isForApp" class="comparison-plan-section__in-app-purchase-description">
          {{ $tc('SignupPage.SelectPlanPage.comparison.inAppPurchaseDescription') }}
        </p>
        <p v-if="isAndroid" class="comparison-plan-section__point-achieve-description">
          {{ $tc('SignupPage.SelectPlanPage.comparison.pointAchieveDescription') }}
        </p>
        <div class="comparison-plan-section__official-SFgo">
          <atom-link
            v-if="isAndroid"
            :url="officialSFgoLink"
            :linkText="$tc('MypagePage.MypageSwitchToPaidPlanPage.officialSFgoLink')"
            :isOpenAsBlank="true"
            :imgPath="require('@/assets/img/icon/icon_external_link_black.svg')"
            class="comparison-plan-section__official-SFgoLink"
          />
        </div>
      </template>
    </div>

    <comparison-plan-table-parts v-if="!isDisplayComparisonPlanInModal" />
    <div v-else class="comparison-plan-section__body">
      <atom-button
        :linkText="$tc('MypagePage.MypageSwitchToPaidPlanPage.planTableTitle')"
        colorType="secondary"
        @onClick="showPlanModal = true"
      />
    </div>

    <div class="comparison-plan-section__modal" v-if="showPlanModal">
      <comparison-plan-modal-section @close="showPlanModal = false" />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from '@vue/composition-api'
import ComparisonPlanTableParts from '@/components/common/MembershipPlan/ComparisonPlanTableParts.vue'
import AtomLink from '@/components/atoms/AtomLink.vue'
import DeviceInfo from '@/util/DeviceInfo'
import Const from '@/util/Const'
import I18n from '@/locales/I18n'
import FreeTrialNoticeParts from '@/components/SignupPage/common/FreeTrialNoticeParts.vue'
import AtomButton from '@/components/atoms/AtomButton.vue'
import ComparisonPlanModalSection from '@/components/common/MembershipPlan/ComparisonPlanModalSection.vue'

/**
 * 新規会員登録 プラン比較
 */
export default defineComponent({
  name: 'ComparisonPlanSection',
  components: {
    ComparisonPlanModalSection,
    AtomButton,
    FreeTrialNoticeParts,
    ComparisonPlanTableParts,
    AtomLink,
  },
  props: {
    /**
     * 表示するタイトル
     */
    sectionTitle: {
      type: String,
      default: '',
    },
    /**
     * 無料トライアルのお知らせを表示するかどうか
     */
    showTrialNotice: {
      type: Boolean,
      default: false,
    },
    /**
     * トライアル広告ページかどうか
     */
    isTrialAds: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    // 機能比較表モーダルを表示するかどうか
    const showPlanModal = ref(false)

    // SFgo公式サイトへのリンクを表示するかどうか
    const isAndroid = computed(() => DeviceInfo.isAndroid())

    const officialSFgoLink =
      I18n.locale === 'ja' ? Const.EXTERNAL_LINKS.ABOUT_SFGO.JA : Const.EXTERNAL_LINKS.ABOUT_SFGO.EN

    // 使用端末がアプリかどうか
    const isForApp = computed(() => DeviceInfo.isiOS() || DeviceInfo.isAndroid())

    /**
     * プラン比較表をモーダルで表示するかどうか
     * トライアル広告の場合、下の部分の高さが出てしまう。その影響でスマホだと表示領域が狭くなるため、トライアル広告 かつ スマホの場合はモーダルで表示する。
     */
    const isDisplayComparisonPlanInModal = computed(
      () => props.isTrialAds && DeviceInfo.isSmartPhone(),
    )

    return {
      showPlanModal,
      isAndroid,
      officialSFgoLink,
      isForApp,
      isDisplayComparisonPlanInModal,
    }
  },
})
</script>

<style lang="scss" scoped>
.comparison-plan-section {
  width: 100%;
  padding: 24px 16px;

  &__trial {
    margin-top: 22px;
    margin-bottom: 46px;
  }

  &__title {
    // 「アプリ内課金の場合、ポイントは付与されません。」の文言を復活させる場合、margin-bottom: 8px; に戻す
    margin-bottom: 24px;
    font-weight: 700;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    letter-spacing: 0.02em;
    font-feature-settings: 'palt' on;
    color: $black;
  }

  &__in-app-purchase-description {
    margin-bottom: 8px;
    font-weight: 500;
    font-size: 13px;
    line-height: 20px;
    text-align: center;
    letter-spacing: 0.02em;
    font-feature-settings: 'palt' on;
    color: $red;
  }

  &__point-achieve-description {
    margin-bottom: 24px;
    font-weight: 500;
    font-size: 12px;
    line-height: 20px;
    text-align: center;
    letter-spacing: 0.02em;
    font-feature-settings: 'palt' on;
    color: $gray75;
  }

  &__official-SFgo {
    margin-bottom: 24px;
    font-weight: 700;
    font-size: 12px;
    line-height: 20px;
    text-align: center;
    letter-spacing: 0.02em;
    font-feature-settings: 'palt' on;

    &Link {
      color: $black80;
      text-decoration-color: rgba($black80, 0.5);
    }
  }

  &__body {
    ::v-deep {
      .atom-btn {
        height: 48px;
        font-size: 17px;
        font-weight: 500;
      }
    }
  }
}
</style>
